import ProductReview from '@aboutyou/legacy-shared/src/pages/ProductReview/ProductReview'
import ProductReviewTranslationsProvider from '@aboutyou/legacy-shared/src/pages/ProductReview/context/ProductReviewTranslationsProvider'
import useProductReviews from '@aboutyou/legacy-shared/src/pages/ProductReview/hooks/useProductReviews'
import { ClientSideNavigationProvider } from '@aboutyou/legacy-shared/src/ui/Navigation/ClientSideLink/ClientSideLink'
import { useScript } from '@aboutyou/legacy-shared/src/utils/hooks/useScript'
import { useIsomorphicLayoutEffect } from 'helpers/hooks/useIsomorphicLayoutEffect'
import useShowLoginPrompt from 'helpers/hooks/useShowLoginPrompt'
import { useTranslation } from 'helpers/hooks/useTranslation'
import { useRouter } from 'helpers/navigation'
import { useObserver } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import Helmet from 'react-helmet'
import { useUserStore } from 'store/context'
import styled from 'styled-components'
import AccordionSection from 'uiComponents/atoms/AccordionSection/AccordionSection'
import {
  bordersColor,
  complementaryColor,
  darkGrayColor,
} from 'uiComponents/constants/colors'
import BackButton from './components/BackButton'
import useProductIdFromPath from './hooks/useProductIdFromPath'
import useProductReviewTranslations from './hooks/useProductReviewTranslations'

const Wrapper = styled.div`
  width: 574px;
  margin: 26px auto 16px;
  border-radius: 2px;
  border: solid 1px ${darkGrayColor};
`

const StyledText = styled.div`
  ul {
    color: ${darkGrayColor};
    font-size: 12px;
  }
`

const GuidelinesText = () => {
  const { t } = useTranslation()

  return (
    <StyledText
      dangerouslySetInnerHTML={{
        __html: t(
          '<p>Unsere Tipps:</p><ul><li>Gib an warum Dir ein Produkt gefällt oder nicht gefällt – z. B. tolle Farbe, sehr bequem etc.</li><li>Konzentrier Dich aufs Produkt – wenn Du ein Problem hattest (z. B. bei der Lieferung), dann wende Dich bitte an unseren Kundenservice, der Dir gerne weiterhilft</li><li>Vermeide Kommentare zum Preis oder zu Sonderangeboten</li><li>Fass Dich kurz – ein paar Details sind super, aber zu lange Bewertungen werden oft nicht gelesen</li><li>Achte auf Deine Ausdrucksweise. Benutze keine Schimpfwörter oder obszöne Ausdrücke, keine faschistische, rassistische, antisemitische, ausländerfeindliche oder ähnliche politisch radikale Äußerungen</li><li>Veröffentliche keine persönlichen Informationen über Dich in Bewertungen, wie Telefonnummern, E-Mail-Adressen usw.</li></ul><p><strong>Das war’s! Wir freuen uns, dass Du anderen Kunden hilfst.</strong></p>',
        ),
      }}
    ></StyledText>
  )
}

const GuidelinesHeading = () => {
  const { t } = useTranslation()
  return <div>{t('Richtlinien Produktbewertungen')}</div>
}

const StyledAccordionSection = styled(AccordionSection)`
  background: ${complementaryColor};
  border: 1px solid ${bordersColor};
  ul {
    list-style-type: disc;
    margin-left: 40px;
  }
  p,
  li {
    margin-bottom: 16px;
  }
`

const FluidContainer = styled.div`
  position: relative;
`

const HelmetTitle: React.FC<{ productId?: number }> = ({ productId }) => {
  const { t } = useTranslation()
  const { productReview } = useProductReviews(productId)
  return (
    <Helmet>
      <title>
        {t('Deine Bewertung für {{product}} | ABOUT YOU', {
          product: productReview?.productName,
        })}
      </title>
      <meta name="robots" content="noindex, follow" />
    </Helmet>
  )
}

const ProductReviewPage: React.FC = () => {
  const translations = useProductReviewTranslations()
  const showLoginPrompt = useShowLoginPrompt()
  const { navigateClient } = useRouter()
  const productId = useProductIdFromPath()
  const userStore = useUserStore()
  const userData = useObserver(() => userStore.data)

  useIsomorphicLayoutEffect(() => {
    if (window) {
      window.IGLOO = window.IGLOO || {
        // @ts-ignore
        enable_rip: true, // Enable Real IP protection.
        enable_flash: false, // Disable flash
        install_flash: false, // Don't ask user to install flash
        loader: {
          version: 'general5', // Non-experimental 5.x updates
          fp_static: false,
        },
      }
    }
  })
  useScript(`/iovations.js`)

  useEffect(() => {
    if (userData && !userData?.id) {
      showLoginPrompt('legacy-product-reviews')
    }
  }, [showLoginPrompt, userData])

  const displayName = userData?.firstname ? `${userData?.firstname}` : ''

  if (!productId || !userData) {
    return null
  }

  return (
    <ClientSideNavigationProvider navigateClient={navigateClient}>
      <HelmetTitle productId={productId} />
      <ProductReviewTranslationsProvider value={translations}>
        <FluidContainer>
          <Wrapper>
            <BackButton productId={productId} />
            <ProductReview productId={productId} displayName={displayName}>
              <StyledAccordionSection
                headline={<GuidelinesHeading />}
                description={<GuidelinesText />}
              />
            </ProductReview>
          </Wrapper>
        </FluidContainer>
      </ProductReviewTranslationsProvider>
    </ClientSideNavigationProvider>
  )
}

export default ProductReviewPage
